/**
 * GSAP Text Reveal Animation
 * Wiederverwendbare Text-Animation für Shopify Themes
 * 
 * Usage:
 * 1. HTML mit .text-reveal Klasse und Wort-Structure
 * 2. TextRevealAnimation.init() aufrufen
 * 3. Optional: eigene Konfiguration übergeben
 */

class TextRevealAnimation {
  constructor(options = {}) {
    this.config = {
      // Selektoren
      triggerSelector: '.text-reveal',
      wordSelector: '.text-reveal__word-inner',
      
      // Animation Settings
      mainTitleDuration: 0.8,
      smallTitleDuration: 0.6,
      mainTitleStagger: 0.4,
      smallTitleStagger: 0.3,
      blockDelay: 0.1,
      
      // Easing
      ease: "power3.out",
      
      // Intersection Observer
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
      
      // Debug
      debug: false,
      
      ...options
    };
    
    this.observer = null;
    this.animated = new Set();
  }
  
  /**
   * Initialisiert die Animation
   */
  init() {
    if (typeof gsap === 'undefined') {
      console.warn('GSAP ist nicht geladen. Bitte GSAP CDN einbinden.');
      return;
    }
    
    this.setupIntersectionObserver();
    this.log('Text Reveal Animation initialisiert');
  }
  
  /**
   * Setup Intersection Observer für Performance
   */
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.animated.has(entry.target)) {
          this.animateElement(entry.target);
          this.animated.add(entry.target);
        }
      });
    }, {
      threshold: this.config.threshold,
      rootMargin: this.config.rootMargin
    });
    
    // Alle Text-Reveal Elemente beobachten
    document.querySelectorAll(this.config.triggerSelector).forEach(element => {
      this.observer.observe(element);
    });
  }
  
  /**
   * Animiert ein einzelnes Text-Element
   */
  animateElement(element) {
    const words = element.querySelectorAll(this.config.wordSelector);
    const isMainTitle = element.classList.contains('text-reveal--main');
    const elementIndex = Array.from(document.querySelectorAll(this.config.triggerSelector)).indexOf(element);
    
    if (words.length === 0) {
      this.log('Keine Wörter gefunden in:', element);
      return;
    }
    
    // Animation konfigurieren
    const duration = isMainTitle ? this.config.mainTitleDuration : this.config.smallTitleDuration;
    const staggerAmount = isMainTitle ? this.config.mainTitleStagger : this.config.smallTitleStagger;
    const delay = elementIndex * this.config.blockDelay;
    
    this.log(`Animiere ${words.length} Wörter in Element ${elementIndex + 1}`);
    
    // GSAP Animation
    gsap.to(words, {
      y: 0,
      duration: duration,
      ease: this.config.ease,
      delay: delay,
      stagger: {
        amount: staggerAmount,
        from: "start"
      },
      onComplete: () => {
        this.log(`Animation für Element ${elementIndex + 1} abgeschlossen`);
      }
    });
  }
  
  /**
   * Manuelle Animation eines spezifischen Elements
   */
  animateSpecific(selector, options = {}) {
    const element = document.querySelector(selector);
    if (element) {
      const oldConfig = { ...this.config };
      this.config = { ...this.config, ...options };
      this.animateElement(element);
      this.config = oldConfig;
    }
  }
  
  /**
   * Reset Animation für ein Element
   */
  resetElement(element) {
    const words = element.querySelectorAll(this.config.wordSelector);
    gsap.set(words, { y: "100%" });
    this.animated.delete(element);
    this.log('Element zurückgesetzt:', element);
  }
  
  /**
   * Reset alle Animationen
   */
  resetAll() {
    document.querySelectorAll(this.config.triggerSelector).forEach(element => {
      this.resetElement(element);
    });
    this.log('Alle Animationen zurückgesetzt');
  }
  
  /**
   * Zerstört die Animation und Observer
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.animated.clear();
    this.log('Text Reveal Animation zerstört');
  }
  
  /**
   * Helper: Split Text in Wörter mit HTML-Structure
   */
  static createTextStructure(text) {
    const words = text.split(' ');
    return words.map(word => 
      `<span class="text-reveal__word">
        <span class="text-reveal__word-inner">${word}</span>
      </span>`
    ).join(' ');
  }
  
  /**
   * Helper: Automatisches Setup für einfache Texte
   */
  static autoSetup(selector, textContent) {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      const text = textContent || element.textContent.trim();
      element.innerHTML = `
        <span class="text-reveal__container">
          ${TextRevealAnimation.createTextStructure(text)}
        </span>
      `;
      element.classList.add('text-reveal');
    });
  }
  
  /**
   * Debug Logging
   */
  log(...args) {
    if (this.config.debug) {
      console.log('[TextRevealAnimation]', ...args);
    }
  }
  
  /**
   * Statische Initialisierung mit Standard-Konfiguration
   */
  static init(options = {}) {
    const animation = new TextRevealAnimation(options);
    animation.init();
    return animation;
  }
}

// CSS Styles als String für einfache Integration
TextRevealAnimation.CSS = `
.text-reveal {
  overflow: hidden;
}

.text-reveal__container {
  display: block;
}

.text-reveal__word {
  display: inline-block;
  overflow: hidden;
  margin-right: 0.2em;
  vertical-align: top;
}

.text-reveal__word:last-child {
  margin-right: 0;
}

.text-reveal__word-inner {
  display: inline-block;
  transform: translateY(100%);
  will-change: transform;
}

.text-reveal--main .text-reveal__word-inner {
  transform: translateY(120%);
}
`;

// Shopify Theme Editor Support
if (typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    // Re-initialisiere Animation bei Section-Änderungen
    setTimeout(() => {
      if (window.textRevealAnimation) {
        window.textRevealAnimation.destroy();
      }
      window.textRevealAnimation = TextRevealAnimation.init();
    }, 100);
  });
}

// Export für verschiedene Module-Systeme
if (typeof module !== 'undefined' && module.exports) {
  module.exports = TextRevealAnimation;
} else if (typeof window !== 'undefined') {
  window.TextRevealAnimation = TextRevealAnimation;
}